import Input from "@/app/components/ui/Inputs/Input";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useModalState } from "@/app/hooks/ui_hooks/useModalState";
import useFormValidations from "@/app/hooks/useFormValidations";
import { useForm } from "react-hook-form";
import ModalPrompt from "@/app/components/ui/Modals/Prompt";
import { IMrpUpdatePostBody } from "@/api_framework/api_modals/Inventory";
import { useMRPUpdateAPI } from "@/app/hooks/api_hooks/Inventory/MrpUpdate/useMRPUpdateAPI";
import clsx from "clsx";
import { useState } from "react";
import PWLCalendarComponent from "@/app/components/ui/Calendar/PWLCalendar";
import { format } from "date-fns";

export interface IMrpUpdateModalProps {
  mrp: number;
  reason: string;
  date: string;
}

const MrpUpdateModalContent = ({ formHook, data, forOnlyNumber }) => {
  return (
    <div className="grid grid-cols-[150px_290px] items-center gap-x-16 gap-y-4 pb-6">
      <span className="self-start font-normal text-black text-xs">Lot Details</span>
      <div className="flex flex-col gap-1 text-xs font-medium text-gray-700">
        <p>Lot Number: {data?.lot_number}</p>
        <p>Exp Date: {data?.expiry_date}</p>
        <p>Current Mrp: {data?.current_mrp}</p>
      </div>

      <span className="font-normal text-black text-xs">Updated MRP</span>
      <Input
        register={formHook.register("mrp", {
          ...forOnlyNumber.validations,
          required: "",
          min: {
            value: 1,
            message: "MRP must be a positive number",
          },
          max: {
            value: +data?.mrp,
            message: "Qty must be less than or equal to Good / Sellable Qty",
          },
          validate: (value) => Number(value) > 0 || "MRP must be a positive number",
        })}
        type="number"
        min={1}
        max={+data?.mrp}
        errorMessages={[{ type: "min", message: "MRP must be a positive number" }]}
        className="w-full text-xs rounded-md border-slate-400"
        fieldError={formHook.formState.errors.mrp}
        placeholder="Enter MRP"
      />

      <span className="font-normal text-black text-xs">Updated Expiry</span>
      <PWLCalendarComponent
        showLeftNav={false}
        errorMessages={[{ message: "", type: "required" }]}
        register={formHook.register("date", {
          required: false,
        })}
        fieldError={formHook.formState.errors.date}
        onSelectDate={(date) => {
          formHook.setValue("date", format(date, "MM/dd/yyyy"));
        }}
        containerClassName="w-full"
        showFutureYears
      />

      <span className="flex justify-start items-center gap-1 font-normal text-black text-xs">
        Reason
        <span className="font-normal text-rose-600 text-xs">*</span>
      </span>
      <textarea
        style={{
          overflowY: "scroll",
          scrollbarWidth: "thin",
        }}
        rows={3}
        className={clsx(`rounded-md w-full resize-y border-gray-300 text-xs`)}
        placeholder="Enter Reason"
        {...formHook.register(`reason`, { required: true, minLength: 1 })}
      />
    </div>
  );
};

const MrpUpdateModal = () => {
  const { setInvisible } = useModalActions("MRP_UPDATE_MODAL");
  const modalState = useModalState("MRP_UPDATE_MODAL");
  const { forOnlyNumber } = useFormValidations();
  const { execute: updateMrp } = useMRPUpdateAPI();
  const { unique_row_id, onUpdateSuccess } = modalState;
  const [_selectedReason, setSelectedReason] = useState<string | null>(null);

  const formHook = useForm<IMrpUpdateModalProps>({
    mode: "onChange",
    defaultValues: { mrp: null, date: "", reason: "" },
  });

  const isFormValid = (!!formHook.watch("mrp") || !!formHook.watch("date")) && !!formHook.watch("reason");

  const resetModalState = () => {
    formHook.reset({ mrp: NaN as number, reason: "" });
    setSelectedReason("");
  };

  const handleSubmit = async (data: IMrpUpdateModalProps) => {
    const constructedBody: IMrpUpdatePostBody = {
      unique_row_id: unique_row_id,
      updated_mrp: Number(data?.mrp),
      reason: data?.reason,
      updated_expiry: data?.date || null,
    };
    updateMrp(constructedBody).then(({ status }) => {
      if (status) {
        resetModalState();
        setInvisible();
        onUpdateSuccess();
      }
    });
  };

  return (
    <ModalPrompt
      modalId="MRP_UPDATE_MODAL"
      showIcon={false}
      titleIcon=""
      dialogClassName="sm:my-8 sm:w-full sm:max-w-2xl sm:p-6"
      button={{
        primaryButtonText: "Update",
        secondaryButtonText: "Cancel",
        // px-6 py-2 with no border so the height matches the Cancel button
        primaryButtonClassname: `px-6 py-2 rounded-md justify-center items-center gap-2 flex cursor-pointer bg-sky-900 hover:bg-sky-800`,
        primaryCallback: () => {
          handleSubmit(formHook.getValues());
        },
        secondaryCallback: () => {
          formHook.clearErrors();
          formHook.setValue("mrp", null);
          formHook.setValue("reason", null);
          setInvisible();
        },
        primaryButtonDisabled: !isFormValid,
      }}
      title="Update MRP"
      content={<MrpUpdateModalContent formHook={formHook} forOnlyNumber={forOnlyNumber} data={modalState} />}
    />
  );
};

export default MrpUpdateModal;
